import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  Timestamp,
  onSnapshot,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Search } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import Header from "../Components/Header";

const StatCard = ({
  icon: Icon,
  title,
  value,
  iconColor = "bg-primaryBlue/20",
  textColor = "text-primaryBlue",
}) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-sm border border-[#fefefe20] p-4 hover:shadow-lg transition-shadow duration-200">
    <div className="flex items-center space-x-4">
      <div className={`p-3 rounded-full ${iconColor}`}>
        <Icon className={`w-6 h-6 ${textColor}`} />
      </div>
      <div>
        <p className="text-sm font-medium text-white/60">{title}</p>
        <h3 className="text-2xl text-left font-bold text-white">{value}</h3>
      </div>
    </div>
  </div>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex items-center justify-center px-6 py-4 bg-primary opacity-75 border-t border-[#fefefe20]">
    <button
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className="px-4 py-2 text-xl font-medium text-white/60 disabled:opacity-50 disabled:cursor-not-allowed hover:text-white transition-colors"
    >
      &lt;
    </button>
    <span className="text-sm text-white/60">
      Page {currentPage} of {totalPages}
    </span>
    <button
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="px-4 py-2 text-xl font-medium text-white/60 disabled:opacity-50 disabled:cursor-not-allowed hover:text-white transition-colors"
    >
      &gt;
    </button>
  </div>
);

const TrackLead = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLeads: 0,
    leadsWithEvents: 0,
    leadsWithVideo: 0,
    leadsLast15Days: 0,
  });
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [note, setNote] = useState("");
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedLeadForEvents, setSelectedLeadForEvents] = useState(null);
  const [showEventsModal, setShowEventsModal] = useState(false); 
  const [totalVideos, setTotalVideos] = useState(0);
  const recordsPerPage = 10;

  useEffect(() => {
  const fetchTotalVideos = async () => {
    try {
      const snap = await getDocs(collection(db, 'MegnetsVideos'))
      console.log('Total videos in MegnetsVideos:', snap.docs.length)
      setTotalVideos(snap.docs.length)
    } catch (err) {
      console.error('Error fetching total videos count:', err)
      setTotalVideos(0)
    }
  }
  fetchTotalVideos()
}, [])
  useEffect(() => {
    let unsubscribe;
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const fifteenDaysAgo = Timestamp.fromDate(
          new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
        );

        const q = query(
          collection(db, "go_orracademy_lead"),
          orderBy("createdAt", "desc"),
        );
        console.log("Setting up Firestore listener for leads with query:", q);
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const leadData = querySnapshot.docs.map((docSnap) => {
            const data = docSnap.data() || {};
            console.log("Processing lead document:", docSnap.id, data);
            const fullName =
              [data.prenom, data.nom].filter(Boolean).join(" ").trim() ||
              "Unknown";
            const eventsMap = data.events || {};
            const videoProgressMap = data.videoProgress || {};
            const eventCount = Object.values(eventsMap).reduce(
              (sum, val) => sum + Number(val || 0),
              0,
            );
            const videoEntries = Object.values(videoProgressMap);
            const videosCompleted = videoEntries.filter(
              (v) => v && v.completed,
            ).length;

            return {
              id: docSnap.id,
              // Core identity
              name: fullName,
              email: data.email || "N/A",
              phoneNumber: data.phone || "N/A",
              leadMagnetId: data.leadMagnetId || null,
              createdAt: data.createdAt || null,
              lastEventAt: data.lastEventAt || null,
              // Form fields from LeadForm
              ambition: data.ambition || "",
              profile: data.profile || "",
              coaching: data.coaching || "",
              budget: data.budget || "",
              projectDescription: data.projectDescription || "",
              newsletter: Boolean(data.newsletter),
              website: data.website || "",
              // Tracking aggregates
              eventCount,
              videosCompleted,
              // Raw tracking maps
              rawEvents: eventsMap,
              rawVideoProgress: videoProgressMap,
              eventLog: data.eventLog || [],
              note: data.note || null,
              rawVideoClickCounts: data.videoClickCounts || {},
            };
          });

          const totalLeads = leadData.length;
          const leadsWithEvents = leadData.filter(
            (l) => l.eventCount > 0,
          ).length;
          const leadsWithVideo = leadData.filter(
            (l) => l.videosCompleted > 0,
          ).length;
          const leadsLast15Days = leadData.filter((l) => {
            const ts = l.lastEventAt || l.createdAt;
            return ts && ts.toMillis() > fifteenDaysAgo.toMillis();
          }).length;

          setStats({
            totalLeads,
            leadsWithEvents,
            leadsWithVideo,
            leadsLast15Days,
          });

          setLeads(leadData);
          setLoading(false);
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const filteredLeads = leads.filter(
    (lead) =>
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.leadMagnetId || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredLeads.length / recordsPerPage) || 1;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedLeads = filteredLeads.slice(
    startIndex,
    startIndex + recordsPerPage,
  );

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleLeadRowClick = (lead) => {
    setSelectedLeadForEvents(lead);
    setShowEventsModal(true);
  };

  const handleAddNote = async () => {
    // Note feature not used on this screen anymore; keep handler for future extension.
    toast.error("Notes are not editable on this screen.");
  };

  return (
    <div className="rounded-sm font-poppins ">
      <div className="w-[100%]">
        <Header
          PageName={"Track lead"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
      <div className="mx-auto space-y-4 bg-[#081028]">
        {loading ? (
          <div className="rounded-sm border-[#ffffff55] border h-[90vh] flex justify-center items-center bg-primary opacity-75 font-poppins">
            <div className="animate-spin w-16 h-16 border-4 border-primaryBlue border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <StatCard
                icon={(props) => (
                  <svg
                    {...props}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                )}
                title="Total Leads"
                value={stats.totalLeads}
                iconColor="bg-purple-500/20"
                textColor="text-purple-500"
              />
              <StatCard
                icon={(props) => (
                  <svg
                    {...props}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                )}
                title="Leads With Events"
                value={stats.leadsWithEvents}
                iconColor="bg-red-500/20"
                textColor="text-red-500"
              />
              <StatCard
                icon={(props) => (
                  <svg
                    {...props}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                title="Leads (Last 15 Days)"
                value={stats.leadsLast15Days}
                iconColor="bg-yellow-500/20"
                textColor="text-yellow-500"
              />
            </div>

            <div className="bg-primary opacity-75 rounded-sm overflow-hidden">
              <div className="p-3 px-4 border-b header-container border-[#fefefe20]">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-white">
                    Lead Attempts Details
                  </h2>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="pl-10 pr-4 py-2 bg-white/5 border border-borderColor rounded-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primaryBlue/50"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 w-4 h-4" />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto bg-[#0B1739]  scrollbar-thin scrollbar-thumb-rounded-sm scrollbar-thumb-blue-500">
                <table className="w-full ">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Lead
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Ambition
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Profile
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Coaching
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Budget
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Project Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Newsletter
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Website
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                        Last Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f3f3f318]">
                    {paginatedLeads.map((lead) => (
                      <tr
                        key={lead.id}
                        className="hover:bg-white/5 cursor-pointer"
                        onClick={() => handleLeadRowClick(lead)}
                      >
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            {lead.name}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {lead.email}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {lead.phoneNumber || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap max-w-[10rem]">
                          <div
                            className="text-xs text-white/60 truncate"
                            title={lead.ambition}
                          >
                            {lead.ambition || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {lead.profile || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {lead.coaching || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {lead.budget || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap max-w-[14rem]">
                          <div
                            className="text-xs text-white/60 truncate"
                            title={lead.projectDescription}
                          >
                            {lead.projectDescription || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {lead.newsletter ? "Yes" : "No"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap max-w-[10rem]">
                          <div
                            className="text-xs text-blue-400 truncate"
                            title={lead.website}
                          >
                            {lead.website || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm text-white/60">
                            {lead.createdAt
                              ? new Date(lead.createdAt.toDate()).toDateString()
                              : "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-left whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            {lead.lastEventAt
                              ? new Date(
                                  lead.lastEventAt.toDate(),
                                ).toDateString()
                              : "-"}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
      {showNoteModal && (
        <div className="fixed w-full h-full inset-0 bg-black/50 flex justify-center items-center">
          <div className="bg-[#060B26] border w-[500px] border-gray-500 p-2 py-4 rounded-lg">
            <h3 className="text-lg font-bold mb-4">Add Note</h3>
            <ReactQuill
              value={note}
              onChange={setNote}
              theme="snow"
              className=""
              modules={{
                toolbar: [
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
            />
            <div className="flex justify-end space-x-4 mt-4">
              <button
                onClick={() => setShowNoteModal(false)}
                className="px-4 py-2 bg-gray-300 text-black rounded-lg"
              >
                Close
              </button>
              <button
                onClick={handleAddNote}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      {showEventsModal && selectedLeadForEvents && (
        <div className="fixed w-full h-full inset-0 bg-black/50 flex justify-center items-center">
          <div className="bg-[#060B26] border w-[600px] max-h-[80vh] overflow-y-auto border-gray-500 p-4 rounded-lg text-left">
            <h3 className="text-lg font-bold mb-2 text-white text-left">
              Lead events – {selectedLeadForEvents.name}
            </h3>
            <p className="text-xs text-white/60 mb-4 break-all text-left">
              {selectedLeadForEvents.email}
            </p>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">
                  Events
                </h4>
                {Object.keys(selectedLeadForEvents.rawEvents || {}).length ===
                0 ? (
                  <p className="text-xs text-white/50">
                    No events recorded yet.
                  </p>
                ) : (
                  <ul className="text-xs text-white/80 space-y-1">
                    {Object.entries(selectedLeadForEvents.rawEvents).map(
                      ([eventName, count]) => (
                        <li
                          key={eventName}
                          className="flex justify-between gap-2"
                        >
                          <span className="truncate">{eventName}</span>
                          <span className="font-semibold">{String(count)}</span>
                        </li>
                      ),
                    )}
                    {Object.entries(
                      selectedLeadForEvents.rawVideoClickCounts || {},
                    ).map(([videoId, data]) => (
                      <li
                        key={`click-${videoId}`}
                        className="flex justify-between gap-2"
                      >
                        <span className="truncate">
                           {data.title || videoId}
                        </span>
                        <span className="font-semibold">
                          {String(data.count || 0)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
               <div>
                <h4 className="text-sm font-semibold text-white mb-2">
                  Video Progress
                </h4>
                {Object.keys(selectedLeadForEvents.rawVideoProgress || {}).length === 0 ? (
                  <p className="text-xs text-white/50">No video progress yet.</p>
                ) : (
                  (() => {
                    // Calculate overall progress
                    const videoProgressEntries = Object.values(
                      selectedLeadForEvents.rawVideoProgress || {},
                    )
                    const sumPercent = videoProgressEntries.reduce(
                      (sum, vp) => sum + (vp.progressPercent || 0),
                      0,
                    )
                    const overallPercent =
                      totalVideos > 0 ? Math.round(sumPercent / totalVideos) : 0
 
                    console.log(
                      "Overall percent:", overallPercent,
                      "| totalVideos:", totalVideos,
                      "| sumPercent:", sumPercent,
                    )
 
                    return (
                      <>
                        {/* Overall Progress Bar */}
                        <div className="flex justify-between items-center mb-2 p-2 bg-white/5 rounded-lg">
                          <span className="text-xs font-semibold text-white">
                            Overall Course Progress
                          </span>
                          <span className="text-sm font-bold text-green-400">
                            {overallPercent}%
                          </span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-1.5 mb-4">
                          <div
                            className="bg-green-400 h-1.5 rounded-full transition-all"
                            style={{ width: `${overallPercent}%` }}
                          />
                        </div>
 
                        {/* Per Video Progress */}
                        <ul className="text-xs text-white/80 space-y-3">
                          {Object.entries(
                            selectedLeadForEvents.rawVideoProgress,
                          ).map(([videoId, vp]) => {
                            const percent = vp.progressPercent || 0
                            console.log(
                              "Video:", vp.title || videoId,
                              "| progressPercent:", percent,
                            )
                            return (
                              <li key={videoId} className="flex flex-col gap-1">
                                <div className="flex justify-between gap-2">
                                  <span className="truncate text-white/80">
                                    {vp.title || videoId}
                                  </span>
                                  <span
                                    className={`font-semibold whitespace-nowrap ${
                                      vp.completed
                                        ? "text-green-400"
                                        : "text-blue-400"
                                    }`}
                                  >
                                    {percent}% {vp.completed ? "✓" : ""}
                                  </span>
                                </div>
                                {/* Per video progress bar */}
                                <div className="w-full bg-white/10 rounded-full h-1">
                                  <div
                                    className={`h-1 rounded-full transition-all ${
                                      vp.completed
                                        ? "bg-green-400"
                                        : "bg-blue-400"
                                    }`}
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                              </li>
                            )
                          })}
                        </ul>
                      </>
                    )
                  })()
                )}
                </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowEventsModal(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackLead;
